import React, { createContext, useContext, useState, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useSocket } from './hooks/useSocket';
import Layout from './components/Layout/Layout';
import DashboardPage from './pages/DashboardPage';
import ZonesPage from './pages/ZonesPage';
import ItemsPage from './pages/ItemsPage';
import AnalyticsPage from './pages/AnalyticsPage';

const SocketContext = createContext(null);
const ToastContext = createContext(null);

export function useSocketContext() {
  return useContext(SocketContext);
}

export function useToast() {
  return useContext(ToastContext);
}

function Toast({ toast, onDismiss }) {
  const colorMap = {
    success: 'border-accent-green bg-accent-green/10 text-accent-green',
    error: 'border-accent-red bg-accent-red/10 text-accent-red',
    warning: 'border-accent-amber bg-accent-amber/10 text-accent-amber',
    info: 'border-accent-blue bg-accent-blue/10 text-accent-blue',
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border backdrop-blur-sm animate-slide-up ${colorMap[toast.type] || colorMap.info}`}
    >
      <span className="text-sm flex-1">{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        className="text-text-muted hover:text-text-primary transition-colors"
      >
        ×
      </button>
    </div>
  );
}

function ToastContainer({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

export default function App() {
  const socketData = useSocket();
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }].slice(-5));

    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  React.useEffect(() => {
    if (socketData.lastTransaction) {
      const tx = socketData.lastTransaction;
      const txType = tx.type === 'SELL' ? '📈' : '📉';
      addToast(
        `${txType} ${tx.type} ${tx.quantity || 1}x ${tx.classname || tx.item || 'item'} @ ${tx.zone || 'unknown'}`,
        tx.type === 'SELL' ? 'success' : 'error',
        3000
      );
    }
  }, [socketData.lastTransaction, addToast]);

  return (
    <SocketContext.Provider value={socketData}>
      <ToastContext.Provider value={{ addToast, dismissToast }}>
        <Layout>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/zones" element={<ZonesPage />} />
            <Route path="/zones/:name" element={<ZonesPage />} />
            <Route path="/items" element={<ItemsPage />} />
            <Route path="/items/:classname" element={<ItemsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
          </Routes>
        </Layout>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </ToastContext.Provider>
    </SocketContext.Provider>
  );
}
